import React from 'react';
import { AdContent } from './AdContent';
import { useTranslation } from 'react-i18next';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RepeatIcon from '@mui/icons-material/Repeat';
import SendIcon from '@mui/icons-material/Send';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';

interface AdDraft {
  title: string;
  content: string;
  date: string;
  charCount: number;
  imageUrl?: string;
  ctaText?: string;
  domain?: string;
  companyName?: string;
}

interface AdDraftsProps {
  drafts: AdDraft[];
  platform: string;
}

export const AdDrafts: React.FC<AdDraftsProps> = ({ drafts, platform }) => {
  const { t } = useTranslation();

  const extractAdData = (
    content: string
  ): { title: string; description: string; ctaText?: string } => {
    try {
      const parsedContent = JSON.parse(content);
      return {
        title: parsedContent.title || t('ads.sample_company'),
        description: parsedContent.content || t('ads.sample_ad'),
        ctaText: parsedContent.cta || t('ads.learn_more'),
      };
    } catch {
      const titleMatch = content.match(/Title:(.+?)(\n|$)/i);
      const descriptionMatch = content.match(/Description:(.+?)(\n|$)/i);
      const ctaMatch = content.match(/CTA:(.+?)(\n|$)/i);

      return {
        title: titleMatch ? titleMatch[1].trim() : t('ads.sample_company'),
        description: descriptionMatch ? descriptionMatch[1].trim() : t('ads.sample_ad'),
        ctaText: ctaMatch ? ctaMatch[1].trim() : t('ads.learn_more'),
      };
    }
  };

  const renderHeader = (draft: AdDraft) => {
    const companyName = draft.companyName || t('ads.sample_company');

    if (platform === 'linkedin_ads') {
      return (
        <div className="header border-b border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-sm mr-3 flex items-center justify-center">
              <span className="text-blue-700 dark:text-blue-300 font-bold text-lg">
                {companyName.charAt(0)}
              </span>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm hover:text-blue-600 hover:underline cursor-pointer">
                {companyName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('ads.promoted')}</p>
            </div>
          </div>
        </div>
      );
    }

    if (platform === 'google_ads') {
      return (
        <div className="header p-4 pb-2">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-bold bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 px-1.5 py-0.5 rounded">
              {t('ads.sponsored')}
            </span>
            <span className="text-sm text-green-700 dark:text-green-400">
              {draft.domain || 'example.com'}
            </span>
          </div>
        </div>
      );
    }

    return (
      <div className="header border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center">
          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full mr-3"></div>
          <div>
            <p className="font-medium text-gray-800 dark:text-gray-200">{companyName}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{t('ads.sponsored')}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderFooter = () => {
    if (platform === 'linkedin_ads') {
      return (
        <div className="border-t border-gray-200 dark:border-gray-700 p-1 flex justify-between mt-auto">
          <button className="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-xs px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />
            {t('ads.like')}
          </button>
          <button className="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-xs px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
            {t('ads.comment')}
          </button>
          <button className="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-xs px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <RepeatIcon sx={{ fontSize: 16 }} />
            {t('ads.repost')}
          </button>
          <button className="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-xs px-3 py-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <SendIcon sx={{ fontSize: 16 }} />
            {t('ads.send')}
          </button>
        </div>
      );
    }

    if (platform === 'google_ads') {
      return null;
    }

    return (
      <div className="border-t border-gray-200 dark:border-gray-700 p-2 flex justify-between mt-auto">
        <button className="text-gray-600 dark:text-gray-400 text-sm px-4 py-1">
          <ThumbUpOutlinedIcon
            sx={{ fontSize: 16, marginRight: '4px', verticalAlign: 'text-bottom' }}
          />
          {t('ads.like')}
        </button>
        <button className="text-gray-600 dark:text-gray-400 text-sm px-4 py-1">
          <ChatBubbleOutlineIcon
            sx={{ fontSize: 16, marginRight: '4px', verticalAlign: 'text-bottom' }}
          />
          {t('ads.comment')}
        </button>
        <button className="text-gray-600 dark:text-gray-400 text-sm px-4 py-1">
          <ShareOutlinedIcon
            sx={{ fontSize: 16, marginRight: '4px', verticalAlign: 'text-bottom' }}
          />
          {t('ads.share')}
        </button>
      </div>
    );
  };

  return (
    <div className="mt-8">
      <h2 className="text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-100">
        {t('ads.generated_ad_drafts')}
      </h2>
      <div className="grid grid-cols-3 gap-4">
        {drafts.map((draft, index) => {
          const {
            title: extractedTitle,
            description,
            ctaText: extractedCta,
          } = extractAdData(draft.content);

          const title = draft.title || extractedTitle;
          const ctaText = draft.ctaText || extractedCta;

          return (
            <div
              key={index}
              className={`rounded-lg shadow-md overflow-hidden flex flex-col h-full ${
                platform === 'linkedin_ads'
                  ? 'bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600'
                  : platform === 'google_ads'
                    ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                    : 'bg-white dark:bg-gray-800'
              }`}
            >
              {renderHeader(draft)}
              <div className="p-4 flex-grow">
                <AdContent
                  title={title}
                  description={description}
                  imageUrl={platform === 'google_ads' ? undefined : draft.imageUrl}
                  ctaText={ctaText}
                />
              </div>
              {renderFooter()}
            </div>
          );
        })}
      </div>
    </div>
  );
};
